/**
 * AppShell — основной layout для всех /authed-страниц (дизайн-система КОММА).
 *
 *   ┌──────────┬────────────────────────────────┐
 *   │          │  TopBar (.topbar)              │
 *   │ Sidebar  ├────────────────────────────────┤
 *   │ (.rail)  │  <main> ← <Outlet/>            │
 *   │          │                                │
 *   └──────────┴────────────────────────────────┘
 *
 * Бренд — в сайдбаре, заголовок раздела — в топбаре (контракт support).
 * Заголовок страницы берётся из PageHeader-контекста (или fallback по маршруту).
 */

import { useEffect, useState } from 'react';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { PageHeaderProvider, useResolvedPageHeader } from './pageHeader';

function ShellTopBar({ navOpen, onToggleNav }: { navOpen: boolean; onToggleNav: () => void }) {
  const { location } = useRouterState();
  const header = useResolvedPageHeader(location.pathname);
  return (
    <TopBar
      title={header.title}
      subtitle={header.subtitle}
      actions={header.actions}
      navOpen={navOpen}
      onToggleNav={onToggleNav}
    />
  );
}

export function AppShell() {
  const { location } = useRouterState();
  const [navOpen, setNavOpen] = useState(false);

  // Закрывать мобильный drawer при смене маршрута.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Esc закрывает drawer + блокировка скролла body, пока открыт.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [navOpen]);

  return (
    <PageHeaderProvider>
      <div className={'app' + (navOpen ? ' nav-open' : '')}>
        {navOpen && (
          <div className="rail-overlay" onClick={() => setNavOpen(false)} aria-hidden />
        )}
        <Sidebar onNavigate={() => setNavOpen(false)} />
        <div className="maincol">
          <ShellTopBar navOpen={navOpen} onToggleNav={() => setNavOpen((v) => !v)} />
          <main className="page-anim" key={location.pathname}>
            <Outlet />
          </main>
        </div>
      </div>
    </PageHeaderProvider>
  );
}
